import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, SkipForward, SkipBack, Moon, Lock, Check } from "lucide-react";
import { api, mediaUrl, TYPE_META } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";

export default function Bedtime() {
  const { settings } = useSite();
  const { user } = useAuth();
  const cfg = settings.bedtime || {};
  const [all, setAll] = useState([]);
  const [picked, setPicked] = useState([]);
  const [idx, setIdx] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null);
  const timer = useRef(null);

  const access = `${!!user?.is_member}:${user?.role || ""}`;
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!access) return;
    api.get("/resources", { params: { type: "parent_child,solo" } }).then((r) => {
      setAll(r.data);
      setPicked(r.data.filter((x) => x.audio_url).map((x) => x.id));
    });
  }, [access]);

  const playable = useMemo(() => all.filter((x) => x.audio_url), [all]);
  const queue = playable.filter((x) => picked.includes(x.id));
  const locked = all.filter((x) => !x.audio_url && x.locked);
  const current = queue[idx];
  const src = current ? mediaUrl(current.audio_url) : "";
  const playingRef = useRef(playing);
  playingRef.current = playing;

  useEffect(() => {
    if (!audio.current || !src) return;
    audio.current.src = src;
    if (playingRef.current) audio.current.play().catch(() => setPlaying(false));
  }, [src]);

  const playAll = () => {
    if (!queue.length) return;
    if (idx === -1) {
      setIdx(0);
      setPlaying(true);
      setTimeout(() => audio.current?.play().catch(() => setPlaying(false)), 50);
    } else if (playing) {
      audio.current.pause();
      setPlaying(false);
    } else {
      audio.current.play();
      setPlaying(true);
    }
  };
  const go = (d) => {
    clearTimeout(timer.current);
    const n = idx + d;
    if (n >= 0 && n < queue.length) {
      setIdx(n);
      setPlaying(true);
    }
  };
  const ended = () => {
    if (idx < queue.length - 1) timer.current = setTimeout(() => setIdx((i) => i + 1), (cfg.pause_seconds ?? 3) * 1000);
    else {
      setPlaying(false);
      setIdx(-1);
    }
  };
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div className="bg-bt-primary text-bt-bg" data-testid="bedtime-page">
      <div className="container-bt py-14 sm:py-20">
        <div className="mx-auto max-w-2xl">
          <Moon className="mb-5 opacity-80" size={32} />
          <h1 className="text-4xl sm:text-5xl" style={{ color: "var(--bt-background)" }}>{cfg.title}</h1>
          <p className="mt-4 text-lg opacity-85">{cfg.intro}</p>

          <div className="mt-10 rounded-[28px] bg-white/10 p-6" data-testid="bedtime-player">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-70">{current ? `Now playing · ${idx + 1} of ${queue.length}` : `${queue.length} recordings ready`}</p>
            <p className="mt-2 font-heading text-2xl" data-testid="bedtime-now-playing">{current ? current.title : "Press play and relax"}</p>
            <div className="mt-6 flex items-center gap-3">
              <button onClick={() => go(-1)} disabled={idx <= 0} className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 disabled:opacity-30" aria-label="Previous" data-testid="bedtime-prev"><SkipBack size={18} /></button>
              <button onClick={playAll} disabled={!queue.length} className="flex h-16 flex-1 items-center justify-center gap-2 rounded-full bg-bt-bg text-lg font-semibold text-bt-primary disabled:opacity-50" data-testid="bedtime-play-button">
                {playing ? <><Pause size={20} /> Pause</> : <><Play size={20} /> {idx === -1 ? "Play all" : "Resume"}</>}
              </button>
              <button onClick={() => go(1)} disabled={idx === -1 || idx >= queue.length - 1} className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 disabled:opacity-30" aria-label="Next" data-testid="bedtime-next"><SkipForward size={18} /></button>
            </div>
            <audio ref={audio} onEnded={ended} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)} className="hidden" />
          </div>

          <p className="mb-3 mt-10 text-sm font-semibold opacity-80">Choose your routine</p>
          <ul className="space-y-2" data-testid="bedtime-track-list">
            {playable.map((x) => {
              const on = picked.includes(x.id);
              return (
                <li key={x.id}>
                  <button onClick={() => toggle(x.id)} className={`flex w-full items-center gap-4 rounded-2xl p-4 text-left transition-colors ${on ? "bg-white/15" : "bg-white/5 opacity-60"} ${current?.id === x.id ? "ring-2 ring-[color:var(--bt-background)]" : ""}`} data-testid={`bedtime-track-${x.slug}`}>
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${on ? "border-transparent bg-bt-bg text-bt-primary" : "border-white/40"}`}>{on && <Check size={15} />}</span>
                    <span><span className="block font-medium">{x.title}</span><span className="text-xs opacity-70">{TYPE_META[x.type]}</span></span>
                  </button>
                </li>
              );
            })}
          </ul>
          {locked.length > 0 && (
            <Link to="/membership" className="mt-4 flex items-center gap-3 rounded-2xl border border-dashed border-white/30 p-4 text-sm" data-testid="bedtime-locked-tracks">
              <Lock size={16} /> {locked.length} more recordings with membership
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
